/**
 * The wire contract. Both the API and the PWA import these, so a route and its
 * caller cannot drift apart without a type error.
 */
import { z } from 'zod'

export const MEAL_SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'] as const
export const mealSlotSchema = z.enum(MEAL_SLOTS)
export type MealSlot = (typeof MEAL_SLOTS)[number]

export const SOURCE_KINDS = [
  'user_ocr',
  'user_manual',
  'open_food_facts',
  'usda',
  'scraper',
  'fastfood_builder',
  'recipe',
] as const
export const sourceKindSchema = z.enum(SOURCE_KINDS)
export type SourceKind = (typeof SOURCE_KINDS)[number]

export const BARCODE_SYMBOLOGIES = [
  'ean13',
  'ean8',
  'upc_a',
  'upc_e',
  'itf14',
  'code128',
  'datamatrix',
  'qr',
] as const
export const barcodeSymbologySchema = z.enum(BARCODE_SYMBOLOGIES)

const uuid = z.uuid()
const isoDate = z.iso.date()
const isoDateTime = z.iso.datetime({ offset: true })

/** Per-100 g nutrients. Bounds mirror the database CHECK constraints. */
export const per100gSchema = z.object({
  kcal: z.number().min(0).max(950),
  protein: z.number().min(0).max(100),
  carbs: z.number().min(0).max(100),
  fat: z.number().min(0).max(100),
  sugars: z.number().min(0).max(100).nullable().default(null),
  satFat: z.number().min(0).max(100).nullable().default(null),
  fiber: z.number().min(0).max(100).nullable().default(null),
  salt: z.number().min(0).max(25).nullable().default(null),
})
export type Per100gInput = z.input<typeof per100gSchema>

/** Rejects a label whose parts cannot coexist — an OCR misread, not a food. */
export const validatedPer100gSchema = per100gSchema
  .refine((n) => n.protein + n.carbs + n.fat <= 105, {
    message: 'protein + carbs + fat exceed 100 g per 100 g',
    path: ['protein'],
  })
  .refine((n) => n.sugars == null || n.sugars <= n.carbs + 0.01, {
    message: 'sugars cannot exceed carbohydrates',
    path: ['sugars'],
  })
  .refine((n) => n.satFat == null || n.satFat <= n.fat + 0.01, {
    message: 'saturated fat cannot exceed fat',
    path: ['satFat'],
  })

// ─── Auth ────────────────────────────────────────────────────────────────────

export const credentialsSchema = z.object({
  email: z.email(),
  password: z.string().min(10).max(200),
})

export const registerSchema = credentialsSchema.extend({
  displayName: z.string().min(1).max(80).optional(),
  tz: z.string().min(1).max(64).default('Europe/Vilnius'),
})

export const sessionUserSchema = z.object({
  id: uuid,
  email: z.string(),
  displayName: z.string().nullable(),
  tz: z.string(),
  isAdmin: z.boolean(),
  // The profile travels with the session so the client can compute a resting
  // burn offline; all nullable, because none of it is required to log food.
  heightCm: z.number().nullable().default(null),
  birthDate: z.string().nullable().default(null),
  sex: z.enum(['male', 'female']).nullable().default(null),
  activityFactor: z.number().nullable().default(null),
  // Defaulted so a profile cached before the field existed still parses — and
  // parses to the voice the app was built to speak in.
  tone: z.enum(['neutral', 'sassy']).default('sassy'),
  // When the account was opened. Travels purely so the client can notice an
  // anniversary offline (`beat.year-1`); nullable and defaulted for the same
  // reason as the profile fields — a session cached before this existed must
  // still parse, and an app that cannot date itself simply does not celebrate.
  createdAt: z.string().nullable().default(null),
})
export type SessionUser = z.infer<typeof sessionUserSchema>

export const authResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  user: sessionUserSchema,
})

// ─── Foods & barcodes ────────────────────────────────────────────────────────

/**
 * What a food's "per 100" is measured in. Volume-declared products (drinks,
 * yoghurt drinks, oils sold by the litre) keep their millilitres instead of
 * being renamed into grams they were never weighed in.
 */
export const foodUnitSchema = z.enum(['g', 'ml'])
export type FoodUnit = z.infer<typeof foodUnitSchema>

export const foodSchema = z.object({
  id: uuid,
  nameLt: z.string().nullable(),
  nameEn: z.string().nullable(),
  brand: z.string().nullable(),
  ...per100gSchema.shape,
  servingSizeG: z.number().positive().nullable(),
  servingName: z.string().nullable(),
  /**
   * What one of them weighs, when the food is one of the ones people count.
   * Null means "weighed", which is nearly everything — see the 0021 migration
   * for why this is not `servingSizeG` under another name.
   */
  pieceWeightG: z.number().positive().max(10000).nullable().default(null),
  unit: foodUnitSchema.default('g'),
  category: z.string().nullable().default(null),
  isVerified: z.boolean(),
  visibility: z.enum(['public', 'private']),
  updatedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
})
export type Food = z.infer<typeof foodSchema>

export const foodInputSchema = z
  .object({
    id: uuid.optional(),
    nameLt: z.string().min(1).max(200).nullable().default(null),
    nameEn: z.string().min(1).max(200).nullable().default(null),
    brand: z.string().max(120).nullable().default(null),
    servingSizeG: z.number().positive().max(10000).nullable().default(null),
    servingName: z.string().max(80).nullable().default(null),
    pieceWeightG: z.number().positive().max(10000).nullable().default(null),
    unit: foodUnitSchema.default('g'),
    visibility: z.enum(['public', 'private']).default('public'),
    sourceKind: sourceKindSchema.default('user_manual'),
  })
  .extend(per100gSchema.shape)
  .refine((f) => f.nameLt != null || f.nameEn != null, {
    message: 'a food needs at least one name',
    path: ['nameLt'],
  })

/** Barcode binding created by the scan → OCR → name → save loop. */
export const barcodeBindingSchema = z.object({
  code: z.string().min(1).max(4096),
  symbology: barcodeSymbologySchema,
})

export const barcodeLookupResponseSchema = z.object({
  tier: z.enum(['local', 'off', 'usda', 'none']),
  food: foodSchema.nullable(),
  /** Present on a miss so the client can hold it through the OCR flow. */
  code: z.string(),
})
export type BarcodeLookupResponse = z.infer<typeof barcodeLookupResponseSchema>

// ─── Diary ───────────────────────────────────────────────────────────────────

export const macroTotalsSchema = z.object({
  kcal: z.number().min(0),
  protein: z.number().min(0),
  carbs: z.number().min(0),
  fat: z.number().min(0),
  sugars: z.number().min(0).nullable(),
  satFat: z.number().min(0).nullable(),
  fiber: z.number().min(0).nullable(),
  salt: z.number().min(0).nullable(),
})

/**
 * A diary row. The macro totals are a snapshot taken when the entry was created
 * — editing the food later must never rewrite what was already eaten.
 */
export const logEntrySchema = z.object({
  id: uuid,
  logDate: isoDate,
  loggedAt: isoDateTime.nullable(),
  loggedTz: z.string().max(64).nullable(),
  slot: mealSlotSchema,
  foodId: uuid.nullable(),
  recipeId: uuid.nullable(),
  fastfoodBuildId: uuid.nullable(),
  quantityG: z.number().positive().max(20000),
  servings: z.number().positive().nullable(),
  snapshotName: z.string().min(1).max(200),
  ...macroTotalsSchema.shape,
  notes: z.string().max(1000).nullable(),
  clientEditedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
})
export type LogEntry = z.infer<typeof logEntrySchema>

// ─── Recipes ─────────────────────────────────────────────────────────────────

/** Mirrors COOKING_METHODS in cooking.ts and the ri_cooking_valid CHECK. */
export const cookingMethodSchema = z.enum([
  'raw',
  'boiled',
  'steamed',
  'oven',
  'grilled',
  'pan',
  'deep_fried',
])

export const recipeIngredientSchema = z.object({
  id: uuid,
  foodId: uuid,
  rawWeightG: z.number().positive().max(100000),
  position: z.number().int().min(0).default(0),
  // Null is "not stated", which is not the same as 'raw': a recipe written
  // before the question existed should not start claiming nothing was cooked.
  cooking: cookingMethodSchema.nullable().default(null),
  deletedAt: isoDateTime.nullable().default(null),
})

/**
 * A recipe's own per-100 g figures, on the wire.
 *
 * Derived data: every ingredient's macros divided by the finished weight, and
 * the server recomputes them from the ingredient rows inside the same
 * transaction that stores them. What arrives here is a hint, never the record.
 *
 * So bounding it like a food label — no food is 200 g of carbohydrate per 100 g
 * — stops nothing and costs everything: the whole push is one schema parse, a
 * single out-of-range recipe rejects the entire body, and the pull runs after
 * the push. One bad row and the phone has stopped syncing in both directions,
 * with no way back, because the row it cannot send is the row it needs
 * corrected. The bounds here are what the columns hold, nothing more.
 */
const derivedPer100gSchema = z.object({
  kcal: z.number().min(0).max(99999),
  protein: z.number().min(0).max(9999),
  carbs: z.number().min(0).max(9999),
  fat: z.number().min(0).max(9999),
  sugars: z.number().min(0).max(9999).nullable().default(null),
  satFat: z.number().min(0).max(9999).nullable().default(null),
  fiber: z.number().min(0).max(9999).nullable().default(null),
  salt: z.number().min(0).max(999).nullable().default(null),
})

export const recipeSchema = z.object({
  id: uuid,
  name: z.string().min(1).max(200),
  notes: z.string().max(2000).nullable(),
  cookedWeightG: z.number().positive().max(100000),
  ...derivedPer100gSchema.shape,
  macrosStale: z.boolean().default(false),
  clientEditedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
  ingredients: z.array(recipeIngredientSchema).default([]),
})
export type Recipe = z.infer<typeof recipeSchema>

export const recipeInputSchema = z.object({
  id: uuid.optional(),
  name: z.string().min(1).max(200),
  notes: z.string().max(2000).nullable().default(null),
  cookedWeightG: z.number().positive().max(100000),
  ingredients: z
    .array(
      z.object({
        id: uuid.optional(),
        foodId: uuid,
        rawWeightG: z.number().positive().max(100000),
        position: z.number().int().min(0).default(0),
        cooking: cookingMethodSchema.nullable().default(null),
      }),
    )
    .min(1),
})

// ─── Fast-food builder ───────────────────────────────────────────────────────

export const fastfoodComponentSchema = z.object({
  id: z.number().int(),
  chainId: z.number().int(),
  chainSlug: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  unitWeightG: z.number().positive().nullable(),
  ...per100gSchema.shape,
  isActive: z.boolean(),
})

export const fastfoodBuildInputSchema = z.object({
  id: uuid.optional(),
  chainId: z.number().int(),
  /** The menu item this started from, when it started from one. */
  itemId: z.number().int().nullable().optional(),
  name: z.string().min(1).max(200),
  items: z
    .array(
      z.object({
        componentId: z.number().int(),
        quantity: z.number().positive().max(50).default(1),
      }),
    )
    .min(1),
})

/**
 * A menu item as published, with everything the builder needs to open it:
 * what it comes with, what can be swapped, and which alternatives belong to
 * the same choice.
 */
export const fastfoodItemComponentSchema = z.object({
  componentId: z.number().int(),
  name: z.string(),
  category: z.string().nullable(),
  weightG: z.number().positive().nullable(),
  unitWeightG: z.number().positive().nullable(),
  /** The part's own unit: milk is poured into a latte, sugar is spooned in. */
  unit: foodUnitSchema.default('g'),
  /** Priced by the portion, with no weight published: show "1 porcija". */
  portionOnly: z.boolean().default(false),
  mandatory: z.boolean(),
  isDefault: z.boolean(),
  choiceGroup: z.string().nullable(),
  position: z.number().int(),
  ...per100gSchema.shape,
})

export const fastfoodItemSchema = z.object({
  id: z.number().int(),
  chainId: z.number().int(),
  chainSlug: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  groupKey: z.string().nullable(),
  sizeLabel: z.string().nullable(),
  portionWeightG: z.number().positive().nullable(),
  /** 'ml' for anything poured; a coffee measured in grams reads as nonsense. */
  unit: foodUnitSchema.default('g'),
  isActive: z.boolean(),
  components: z.array(fastfoodItemComponentSchema),
})

/**
 * The cup sizes a counter pours, which are not the ones the chain publishes.
 * Ordered smallest first; empty when the chain sells one size of everything.
 */
export const drinkSizeSchema = z.object({
  label: z.string().min(1).max(8),
  ml: z.number().int().min(50).max(2000),
})
export type DrinkSize = z.infer<typeof drinkSizeSchema>
export type FastfoodItem = z.infer<typeof fastfoodItemSchema>

// ─── Goals & wearable ────────────────────────────────────────────────────────

export const userGoalSchema = z.object({
  id: uuid,
  effectiveFrom: isoDate,
  kcalTarget: z.number().int().min(500).max(10000),
  proteinG: z.number().min(0).nullable(),
  carbsG: z.number().min(0).nullable(),
  fatG: z.number().min(0).nullable(),
  fiberG: z.number().min(0).nullable(),
  // Ceilings, unlike the four above. Direction lives in `targets.ts`, not here.
  sugarsG: z.number().min(0).nullable().default(null),
  satFatG: z.number().min(0).nullable().default(null),
  saltG: z.number().min(0).nullable().default(null),
  clientEditedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
})

export const wearableDailySchema = z.object({
  summaryDate: isoDate,
  provider: z.enum(['manual', 'gadgetbridge', 'huawei', 'huawei_ocr']).default('manual'),
  activeKcal: z.number().min(0).max(20000).nullable(),
  restingKcal: z.number().min(0).max(20000).nullable(),
  steps: z.number().int().min(0).max(200000).nullable(),
})

// ─── Profile, body, workouts ─────────────────────────────────────────────────

export const sexSchema = z.enum(['male', 'female'])

/** Everything Mifflin-St Jeor needs, all optional — the app works without it. */
export const profilePatchSchema = z.object({
  displayName: z.string().min(1).max(120).nullable().optional(),
  tz: z.string().min(1).max(64).optional(),
  heightCm: z.number().min(50).max(260).nullable().optional(),
  birthDate: isoDate.nullable().optional(),
  sex: sexSchema.nullable().optional(),
  activityFactor: z.number().min(1).max(2.5).nullable().optional(),
  tone: z.enum(['neutral', 'sassy']).optional(),
})
export type ProfilePatch = z.infer<typeof profilePatchSchema>

export const BODY_MEASUREMENT_SOURCES = ['manual', 'scale'] as const
export const bodyMeasurementSourceSchema = z.enum(BODY_MEASUREMENT_SOURCES)
export type BodyMeasurementSource = (typeof BODY_MEASUREMENT_SOURCES)[number]

export const bodyMeasurementSchema = z
  .object({
    id: uuid,
    measuredOn: isoDate,
    weightKg: z.number().min(20).max(400).nullable(),
    bodyFatPct: z.number().min(0).max(75).nullable(),
    waistCm: z.number().min(30).max(250).nullable(),
    // Everything a smart scale contributes is optional rather than defaulted,
    // and the distinction is load-bearing during a rollout. A phone still
    // running the previous build pushes a row without these keys; `.optional()`
    // leaves them absent so the server can skip them, while a `.default(null)`
    // would turn every edit from that phone into an instruction to erase the
    // scale's readings.
    muscleKg: z.number().min(0).max(200).nullable().optional(),
    waterPct: z.number().min(0).max(100).nullable().optional(),
    impedanceOhm: z.number().int().min(100).max(3000).nullable().optional(),
    // The 250 kHz reading. Carried so the raw measurement survives a round
    // trip through a phone; nothing derives anything from it yet.
    impedanceHighOhm: z.number().int().min(100).max(3000).nullable().optional(),
    proteinPct: z.number().min(0).max(100).nullable().optional(),
    // Litres of water outside and inside the cells. Present only when the
    // scale sent both frequencies; their sum is total body water.
    ecwL: z.number().min(0).max(200).nullable().optional(),
    icwL: z.number().min(0).max(200).nullable().optional(),
    source: bodyMeasurementSourceSchema.optional(),
    note: z.string().max(500).nullable(),
    clientEditedAt: isoDateTime,
    deletedAt: isoDateTime.nullable(),
  })
  // A row measuring nothing is a sync artefact; the database rejects it too.
  .refine(
    (m) => m.deletedAt != null || m.weightKg != null || m.bodyFatPct != null || m.waistCm != null,
    { message: 'a measurement needs at least one value' },
  )
export type BodyMeasurement = z.infer<typeof bodyMeasurementSchema>

export const WORKOUT_KINDS = [
  'walk',
  'run',
  'ride',
  'swim',
  'gym',
  'hiit',
  'sport',
  'other',
] as const
export const workoutKindSchema = z.enum(WORKOUT_KINDS)
export type WorkoutKind = (typeof WORKOUT_KINDS)[number]

export const WORKOUT_SOURCES = [
  'manual',
  'gadgetbridge',
  'huawei_ocr',
  'healthkit',
  'import',
] as const

export const workoutSchema = z.object({
  id: uuid,
  logDate: isoDate,
  startedAt: isoDateTime.nullable(),
  kind: workoutKindSchema.default('other'),
  name: z.string().max(200).nullable(),
  durationMin: z.number().positive().max(1440).nullable(),
  kcal: z.number().min(0).max(20000).nullable(),
  distanceKm: z.number().min(0).max(1000).nullable(),
  avgHr: z.number().int().min(21).max(259).nullable(),
  steps: z.number().int().min(0).nullable(),
  source: z.enum(WORKOUT_SOURCES).default('manual'),
  /** The exporter's id: what makes a re-import an update, not a duplicate. */
  externalId: z.string().max(200).nullable().default(null),
  notes: z.string().max(1000).nullable().default(null),
  clientEditedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
})
export type Workout = z.infer<typeof workoutSchema>

export const AWARD_KINDS = ['trophy', 'beat'] as const
export const awardKindSchema = z.enum(AWARD_KINDS)
export type AwardKind = (typeof AWARD_KINDS)[number]

/**
 * One earned trophy or one-shot story beat, frozen at the moment it fired.
 * `meta` is whatever figures the line quoted (a streak length, a date) — kept
 * flat and primitive-only, since it exists to be read back verbatim, not to
 * carry structure.
 */
export const awardSchema = z.object({
  id: uuid,
  kind: awardKindSchema,
  /** The trophy or beat's own stable id, e.g. "log-100", "beat.streak-30". */
  awardKey: z.string().min(1).max(100),
  awardedOn: isoDate,
  meta: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).default({}),
  clientEditedAt: isoDateTime,
  deletedAt: isoDateTime.nullable(),
})
export type Award = z.infer<typeof awardSchema>

// ─── Sync ────────────────────────────────────────────────────────────────────

/**
 * Delta sync. Clients generate row ids so they can write offline; the server
 * orders every change on a single sequence and hands back a cursor. Conflicts
 * resolve last-write-wins on clientEditedAt, which the server never rewrites.
 */
export const syncPushSchema = z.object({
  deviceId: uuid,
  logEntries: z.array(logEntrySchema).max(500).default([]),
  recipes: z.array(recipeSchema).max(200).default([]),
  goals: z.array(userGoalSchema).max(50).default([]),
  wearableDaily: z.array(wearableDailySchema).max(90).default([]),
  bodyMeasurements: z.array(bodyMeasurementSchema).max(365).default([]),
  workouts: z.array(workoutSchema).max(500).default([]),
  awards: z.array(awardSchema).max(200).default([]),
})
export type SyncPush = z.infer<typeof syncPushSchema>

export const syncPullQuerySchema = z.object({
  cursor: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(2000).default(500),
})

export const syncPullResponseSchema = z.object({
  cursor: z.number().int(),
  hasMore: z.boolean(),
  foods: z.array(foodSchema),
  barcodes: z.array(
    z.object({
      code: z.string(),
      symbology: barcodeSymbologySchema,
      foodId: uuid,
      deletedAt: isoDateTime.nullable(),
    }),
  ),
  logEntries: z.array(logEntrySchema),
  recipes: z.array(recipeSchema),
  goals: z.array(userGoalSchema),
  wearableDaily: z.array(wearableDailySchema),
  bodyMeasurements: z.array(bodyMeasurementSchema),
  workouts: z.array(workoutSchema),
  awards: z.array(awardSchema),
})
export type SyncPullResponse = z.infer<typeof syncPullResponseSchema>

/** Barcode pushes resolve rather than fail: the server names the winner. */
export const barcodeBindResponseSchema = z.object({
  code: z.string(),
  foodId: uuid,
  /** True when another device had already bound this code to a different food. */
  remapped: z.boolean(),
})

// ─── OCR & voice ─────────────────────────────────────────────────────────────

export const ocrParseResponseSchema = z.object({
  engine: z.enum(['gemini', 'tesseract']),
  productName: z.string().nullable(),
  brand: z.string().nullable(),
  language: z.string().nullable(),
  servingSizeG: z.number().positive().nullable(),
  /** Whether the panel declared its hundred in grams or in millilitres. */
  unit: foodUnitSchema.default('g'),
  per100g: z.object({
    kcal: z.number().nullable(),
    protein: z.number().nullable(),
    carbs: z.number().nullable(),
    fat: z.number().nullable(),
    sugars: z.number().nullable(),
    satFat: z.number().nullable(),
    fiber: z.number().nullable(),
    salt: z.number().nullable(),
  }),
  confidence: z.number().min(0).max(1),
  suspect: z.array(z.string()).default([]),
})
export type OcrParseResponse = z.infer<typeof ocrParseResponseSchema>

/**
 * A recording, for the devices that cannot turn speech into words themselves.
 *
 * Apple's dictation stack has no Lithuanian, so on an iPhone the browser's own
 * recogniser either refuses the language or returns nothing at all; the phone
 * sends what it heard instead and the model does the listening. Capped at four
 * megabytes of base64, which is minutes of speech at any codec a phone uses and
 * still an order of magnitude under the upload limit.
 */
export const voiceAudioSchema = z.object({
  mimeType: z.string().min(5).max(120),
  base64: z.string().min(64).max(4_000_000),
})

export const voiceParseRequestSchema = z
  .object({
    transcript: z.string().min(1).max(2000).optional(),
    audio: voiceAudioSchema.optional(),
    lang: z.enum(['lt', 'en']).default('lt'),
  })
  .refine((body) => body.transcript != null || body.audio != null, {
    message: 'either transcript or audio is required',
  })

export const voiceParseResponseSchema = z.object({
  items: z.array(
    z.object({
      name: z.string(),
      quantity: z.number().positive(),
      unit: z.enum(['g', 'ml', 'vnt']),
      modifier: z.string().nullable().default(null),
      candidates: z
        .array(z.object({ food: foodSchema, similarity: z.number() }))
        .max(3)
        .default([]),
    }),
  ),
})

/**
 * What a photo of a plate or a piece of produce turned into. `portionG` is the
 * model's read of how much is in front of the camera — a starting quantity for
 * the log sheet, never a measurement.
 */
export const photoIdentifyResponseSchema = z.object({
  items: z.array(
    z.object({
      name: z.string(),
      nameEn: z.string().nullable().default(null),
      portionG: z.number().positive().nullable().default(null),
      confidence: z.number().min(0).max(1),
      candidates: z
        .array(z.object({ food: foodSchema, similarity: z.number() }))
        .max(5)
        .default([]),
    }),
  ),
})
export type PhotoIdentifyResponse = z.infer<typeof photoIdentifyResponseSchema>

// ─── Search ──────────────────────────────────────────────────────────────────

export const foodSearchQuerySchema = z.object({
  q: z.string().min(1).max(100),
  limit: z.coerce.number().int().min(1).max(50).default(25),
})

export const errorResponseSchema = z.object({
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
})
